import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Node,
  Edge,
  Viewport,
  ConnectionMode,
  MarkerType,
} from 'reactflow';
import { Loader2, AlertCircle, ExternalLink, Sun, Moon } from '@/components/icons';
import { getPublicDiagram } from '../../services/diagramService';
import { TechNode, GroupNode, StickyNoteNode, TextNode } from '../../features/diagrams';
import { useTheme } from '../../App';
import { Tooltip } from '@/components/ui/beui';
import 'reactflow/dist/style.css';
import { EDGE_TYPES } from '../../features/diagrams';

const nodeTypes = {
  techNode: TechNode,
  groupNode: GroupNode,
  stickyNote: StickyNoteNode,
  textNode: TextNode,
};

interface PublicDiagramData {
  id: string;
  name: string;
  description: string | null;
  nodes: Node[];
  edges: Edge[];
  viewport: Viewport | null;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Slim dark bar mirroring the app TopBar — public variant (no search/account). */
function ShareTopBar({ diagram }: { diagram?: PublicDiagramData | null }) {
  const { isDark, toggleTheme } = useTheme();

  return (
    <header className="z-30 flex h-8 flex-none items-center gap-3 rounded-lg bg-[var(--topbar)] px-1.5 text-[var(--topbar-foreground)] shadow-sm">
      <Link to="/" className="flex w-auto flex-none items-center gap-2 md:w-[190px]">
        <img src="/StackSketch-IconLogo.svg" alt="" className="h-5 w-5 rounded" />
        <span className="hidden text-[13px] font-semibold tracking-tight sm:inline">StackSketch</span>
      </Link>

      <div className="flex min-w-0 flex-1 items-baseline gap-2 md:justify-center">
        {diagram?.name ? (
          // min-w-0 on the anchor keeps the truncate chain intact — without it
          // the inline-flex wrapper refuses to shrink below the full name.
          <Tooltip content={diagram.name} wrapperClassName="min-w-0 max-w-full">
            <h1 className="min-w-0 truncate text-[12.5px] text-[var(--topbar-muted)]">
              {diagram.name}
            </h1>
          </Tooltip>
        ) : (
          <h1 className="min-w-0 truncate text-[12.5px] text-[var(--topbar-muted)]" />
        )}
        {diagram && (
          <span className="hidden flex-none text-xs text-[var(--topbar-muted)] lg:inline">
            · {diagram.nodes.length} nodes · {diagram.edges.length} edges ·{' '}
            {new Date(diagram.updatedAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        )}
      </div>

      <div className="flex w-auto flex-none items-center justify-end gap-2 md:w-[190px]">
        <button
          type="button"
          onClick={toggleTheme}
          aria-label="Toggle theme"
          className="ss-press grid h-[26px] w-[26px] place-items-center rounded-md text-[var(--topbar-muted)] transition-colors hover:bg-white/10 hover:text-[var(--topbar-foreground)]"
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        <Link
          to="/"
          className="ss-press flex h-[26px] items-center gap-1.5 rounded-md bg-primary px-2.5 text-[12.5px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Try Stack Sketch
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>
    </header>
  );
}

function PublicDiagramViewerContent() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [diagram, setDiagram] = useState<PublicDiagramData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDiagram = async () => {
      if (!shareToken) {
        setError('Invalid share link');
        setIsLoading(false);
        return;
      }

      try {
        const data = await getPublicDiagram(shareToken);
        // Remove interactive callbacks from nodes (view-only)
        const viewOnlyNodes = data.nodes.map((node) => ({
          ...node,
          data: {
            ...node.data,
            onSnapshot: undefined,
          },
          draggable: false,
          selectable: false,
          connectable: false,
        }));
        setDiagram({ ...data, nodes: viewOnlyNodes });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Diagram not found or not public');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDiagram();
  }, [shareToken]);

  return (
    <div className="flex h-screen w-full flex-col gap-2 overflow-hidden bg-[var(--topbar)] p-2">
      <ShareTopBar diagram={diagram} />

      <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-background shadow-sm">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading diagram...</p>
            </div>
          </div>
        ) : error || !diagram ? (
          <div className="flex h-full items-center justify-center">
            <div className="mx-4 max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-xl">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <h1 className="mb-2 text-xl font-semibold text-foreground">Diagram Not Found</h1>
              <p className="mb-6 text-muted-foreground">
                {error || 'This diagram may have been deleted or the link may be invalid.'}
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Go to Stack Sketch
                <ExternalLink className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="absolute inset-0">
              <ReactFlow
                nodes={diagram.nodes}
                edges={diagram.edges}
                defaultViewport={diagram.viewport || undefined}
                nodeTypes={nodeTypes}
                edgeTypes={EDGE_TYPES}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={false}
                panOnDrag={true}
                zoomOnScroll={true}
                zoomOnPinch={true}
                preventScrolling={true}
                connectionMode={ConnectionMode.Loose}
                className="bg-background transition-colors duration-200"
                proOptions={{ hideAttribution: true }}
                defaultEdgeOptions={{
                  type: 'smoothstep',
                  style: { strokeWidth: 2 },
                  markerEnd: { type: MarkerType.ArrowClosed },
                }}
              >
                <Background
                  color="var(--border)"
                  gap={16}
                  size={2}
                  variant={BackgroundVariant.Dots}
                />
              </ReactFlow>
            </div>

            {/* View-only badge */}
            <div className="absolute bottom-4 left-4 z-10 rounded-full border border-border bg-card/90 px-3 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur-sm">
              View-only
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function PublicDiagramViewer() {
  return (
    <ReactFlowProvider>
      <PublicDiagramViewerContent />
    </ReactFlowProvider>
  );
}
